/**
 * Build per-lap rows from a SwimFirehoseResponse. One lap = one length when the
 * Apple Watch recorded auto-lap events; one synthetic "whole workout" lap when
 * the workout had no lap events.
 *
 * HKWorkoutEvent.dateInterval semantics: `date` IS the lap start time;
 * `durationSeconds` IS the lap duration. Both are authoritative. A previous
 * implementation computed duration as (prev_end → this_end), which inflated
 * laps that followed a rest gap (Scott's 4-min conversation break made L15
 * read as 285s instead of 22.85s). Rest gaps live in restIntervals.ts.
 */
import type { SwimFirehoseResponse, FirehoseSampleEntry, FirehoseTypeBucket } from "@contract/types";
import { decodeMetadataValue, decodeStrokeStyle, HK_META_KEYS, readMetaValue } from "./healthKitLabels";
import { samplesInLap, averageOf, maxOf, sumOf } from "./_reduce";
import { type CorrectionKey, assignOrdinals, deriveCorrectionKey } from "./correctionKey";

export interface Lap {
  index: number;                  // 1-based
  startMs: number;
  endMs: number;
  durationSeconds: number;
  strokeStyle: string;            // EFFECTIVE stroke (watch value, or the user's correction once applied)
  watchStrokeStyle: string;       // the watch's ORIGINAL classification — never overwritten by a correction
  /** Stroke count for this lap, from HKQuantityTypeIdentifierSwimmingStrokeCount samples. */
  strokeCount: number | null;
  /** SPL = strokes per length. Derived from strokeCount, assuming each lap is one length. */
  spl: number | null;
  /** SWOLF = strokes + duration_seconds. Lower = more efficient. The canonical
   *  swim-efficiency metric, sourced directly from HKSWOLFScore lap metadata. */
  swolf: number | null;
  /** Average HR across samples that fall inside this lap's window. */
  avgHr: number | null;
  /** Max HR observed during this lap. */
  maxHr: number | null;
  /** Lap pool length in meters, from HKMetadataKeyLapLength (constant per workout). */
  lengthMeters: number | null;
  /** Pace per 100m, seconds. */
  pacePer100s: number | null;
  /** Resolved exclusion (D-03): the detection default (rest/wall D-02, >2SD slow-side
   *  outlier D-01) overridden in both directions by a persisted LapLabel (D-04).
   *  Stamped ONCE in deriveWorkout right after buildLaps, by stampEffectiveExcluded.
   *  The five aggregate consumers filter on this; restIntervals keeps the gap (D-05).
   *  Initialized false here; the stamper flips it. */
  effectiveExcluded: boolean;
  /** Why an auto-pass excluded this length, in short jargon-free copy for the UI
   *  (TW8 decision 3). null = counted, OR user re-included it via a label. A
   *  non-null phrase ("Glitch — too short", "Unusually slow", "You marked this
   *  out") names the reason. Initialized null here; the stamper sets it. */
  exclusionReason: string | null;
  /** Stable composite identity key for durable stroke correction persistence (D1).
   *  Computed from (offsetSeconds, durationSeconds, duplicateOrdinal); order-independent
   *  across re-fetches. `index` remains display/debug only. */
  correctionKey: CorrectionKey;
}

/**
 * Resolve pool length from lap-event metadata (constant per workout), falling
 * back to (totalDistance / lapCount) when the metadata key is absent.
 * Returns null when neither signal is present — buildLaps then yields a single
 * synthetic lap with `lengthMeters = totalDistance`.
 */
export function detectPoolLength(
  lapEvents: SwimFirehoseResponse["events"],
  totalDistanceMeters: number,
): number | null {
  for (const ev of lapEvents) {
    const raw = readMetaValue(ev.metadata, HK_META_KEYS.lapLength);
    if (raw !== undefined) {
      // HealthKit usually stores this as a quantity string like "25 m" — best-effort parse
      const parsed = parseFloat(String(raw));
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
  }
  if (lapEvents.length > 0 && totalDistanceMeters > 0) {
    return Math.round(totalDistanceMeters / lapEvents.length);
  }
  return null;
}

export function buildLaps(
  dump: SwimFirehoseResponse,
  poolLengthMeters: number | null,
): Lap[] {
  const workoutStart = new Date(dump.workout.startDate).getTime();
  const workoutEnd = new Date(dump.workout.endDate).getTime();
  const totalDurationSeconds = dump.workout.durationSeconds;
  const totalDistanceMeters = dump.workout.totalDistance?.value ?? 0;

  const hrBucket = dump.buckets.find((b) => b.typeName === "HKQuantityTypeIdentifierHeartRate") as FirehoseTypeBucket | undefined;
  const strokeCountBucket = dump.buckets.find((b) => b.typeName === "HKQuantityTypeIdentifierSwimmingStrokeCount") as FirehoseTypeBucket | undefined;
  const hrSamples: FirehoseSampleEntry[] = hrBucket?.samples ?? [];
  const strokeCountSamples: FirehoseSampleEntry[] = strokeCountBucket?.samples ?? [];

  // CRITICAL: filter SEGMENTS out. Apple Watch emits `segment` events at structural
  // boundaries (rest gaps, set transitions) — they are NOT laps. Counting them as
  // laps inflates total length count and corrupts peak-lap detection.
  // Sort ascending by start time so lapIndex, row order, and set-grouping are stable
  // even if HealthKit returns events out of order.
  const lapEvents = dump.events
    .filter((e) => e.type === "lap")
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  if (lapEvents.length === 0) {
    // No laps recorded — synthesize one "whole workout" lap so prototypes still render.
    return [
      {
        index: 1,
        startMs: workoutStart,
        endMs: workoutEnd,
        durationSeconds: totalDurationSeconds,
        strokeStyle: "Unknown",
        watchStrokeStyle: "Unknown",
        strokeCount: strokeCountBucket ? sumOf(strokeCountBucket.samples) : null,
        spl: null,
        swolf: null,
        avgHr: hrBucket ? averageOf(hrBucket.samples) : null,
        maxHr: hrBucket ? maxOf(hrBucket.samples) : null,
        lengthMeters: totalDistanceMeters || null,
        pacePer100s: totalDistanceMeters > 0 ? (totalDurationSeconds / totalDistanceMeters) * 100 : null,
        effectiveExcluded: false,
        exclusionReason: null,
        correctionKey: deriveCorrectionKey(workoutStart, workoutStart, totalDurationSeconds, 0),
      },
    ];
  }

  // Derive all lap timing upfront so assignOrdinals can bucket across the full set.
  const lapTimings = lapEvents.map((ev) => ({
    startMs: new Date(ev.date).getTime(),
    durationSeconds: ev.durationSeconds ?? 0,
  }));
  const correctionKeys = assignOrdinals(lapTimings, workoutStart);

  return lapEvents.map((ev, i) => {
    const { startMs, durationSeconds } = lapTimings[i];
    const endMs = startMs + durationSeconds * 1000;

    const strokeStyleRaw = readMetaValue(ev.metadata, HK_META_KEYS.strokeStyle);
    const strokeStyle = strokeStyleRaw !== undefined
      ? decodeMetadataValue("HKMetadataKeySwimmingStrokeStyle", strokeStyleRaw)
      : "Unknown";

    const lapHrSamples = samplesInLap(hrSamples, startMs, endMs);
    const lapStrokeSamples = samplesInLap(strokeCountSamples, startMs, endMs);

    const strokeCount = lapStrokeSamples.length > 0 ? sumOf(lapStrokeSamples) : null;
    const lengthMeters = poolLengthMeters;
    const pacePer100s = lengthMeters && lengthMeters > 0
      ? (durationSeconds / lengthMeters) * 100
      : null;

    const swolfRaw = ev.metadata?.HKSWOLFScore;
    const swolf = typeof swolfRaw === "number" ? Math.round(swolfRaw) : null;

    return {
      index: i + 1,
      startMs,
      endMs,
      durationSeconds,
      strokeStyle,
      watchStrokeStyle: strokeStyle,
      strokeCount,
      spl: strokeCount, // assuming 1 length per lap event
      swolf,
      avgHr: averageOf(lapHrSamples),
      maxHr: maxOf(lapHrSamples),
      lengthMeters,
      pacePer100s,
      effectiveExcluded: false,
      exclusionReason: null,
      correctionKey: correctionKeys[i],
    };
  });
}

// ─── Open water ───────────────────────────────────────────────────────────────

/**
 * True when this workout is an open-water swim. The exporter stamps the
 * open-water flag on each workoutActivity (`swimmingLocationType: "openWater"`),
 * NOT on the top-level workout, so we scan the activities array. Older recorders
 * emit an empty activities array — those (and every pool swim) return false, so
 * the whole open-water branch is opt-in and pool swims are never touched.
 */
export function isOpenWaterFirehose(dump: SwimFirehoseResponse): boolean {
  return dump.activities.some((a) => a.swimmingLocationType === "openWater");
}

/**
 * Build split-laps for an open-water swim from the paired DistanceSwimming ×
 * SwimmingStrokeCount windows.
 *
 * Open water has NO pool lengths and NO `lap` events — instead the Watch emits
 * GPS-derived DistanceSwimming windows (natural "splits"), 83 of 84 of which
 * pair EXACTLY on (startDate, endDate) with a SwimmingStrokeCount window that
 * carries the HKSwimmingStrokeStyle. We join on the identical window key: a
 * distance window with a stroke partner takes that partner's stroke + count; a
 * distance window with NO partner gets stroke "Unknown" and a null count.
 *
 * Each split carries its OWN GPS meters (lap.lengthMeters) and derives pace/100m
 * from it. `swolf` and `spl` stay null (a variable-length GPS split is not a
 * fixed pool length, so strokes-per-length and SWOLF are undefined). The
 * workout-level `poolLengthMeters` stays null — that guard is what keeps open
 * water out of the pool-length record aggregates (allTimeAggregates.ts).
 */
export function buildOpenWaterSplits(dump: SwimFirehoseResponse): Lap[] {
  const workoutStart = new Date(dump.workout.startDate).getTime();

  const distBucket = dump.buckets.find((b) => b.typeName === "HKQuantityTypeIdentifierDistanceSwimming") as FirehoseTypeBucket | undefined;
  const strokeCountBucket = dump.buckets.find((b) => b.typeName === "HKQuantityTypeIdentifierSwimmingStrokeCount") as FirehoseTypeBucket | undefined;
  const hrBucket = dump.buckets.find((b) => b.typeName === "HKQuantityTypeIdentifierHeartRate") as FirehoseTypeBucket | undefined;
  const distSamples: FirehoseSampleEntry[] = distBucket?.samples ?? [];
  const strokeSamples: FirehoseSampleEntry[] = strokeCountBucket?.samples ?? [];
  const hrSamples: FirehoseSampleEntry[] = hrBucket?.samples ?? [];

  // Index stroke windows by their exact (startDate|endDate) key for the join.
  const strokeByWindow = new Map<string, FirehoseSampleEntry>();
  for (const s of strokeSamples) strokeByWindow.set(`${s.startDate}|${s.endDate}`, s);

  // Sort distance windows by start time so split index + ribbon order are stable
  // even if the bucket arrives out of order.
  const sorted = [...distSamples].sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
  );

  const timings = sorted.map((d) => {
    const startMs = new Date(d.startDate).getTime();
    const endMs = new Date(d.endDate).getTime();
    return { startMs, durationSeconds: Math.max(0, (endMs - startMs) / 1000) };
  });
  const correctionKeys = assignOrdinals(timings, workoutStart);

  return sorted.map((d, i) => {
    const { startMs, durationSeconds } = timings[i];
    const endMs = new Date(d.endDate).getTime();
    const meters = typeof d.value === "number" ? d.value : 0;

    const partner = strokeByWindow.get(`${d.startDate}|${d.endDate}`);
    const styleRaw = partner ? readMetaValue(partner.metadata, HK_META_KEYS.strokeStyle) : undefined;
    const strokeStyle = styleRaw !== undefined ? decodeStrokeStyle(styleRaw) : "Unknown";
    const strokeCount = partner && typeof partner.value === "number" ? partner.value : null;

    const lapHrSamples = samplesInLap(hrSamples, startMs, endMs);

    return {
      index: i + 1,
      startMs,
      endMs,
      durationSeconds,
      strokeStyle,
      watchStrokeStyle: strokeStyle,
      strokeCount,
      spl: null, // a GPS split is not a fixed length — strokes-per-length is undefined
      swolf: null,
      avgHr: averageOf(lapHrSamples),
      maxHr: maxOf(lapHrSamples),
      lengthMeters: meters > 0 ? meters : null,
      pacePer100s: meters > 0 ? (durationSeconds / meters) * 100 : null,
      effectiveExcluded: false,
      exclusionReason: null,
      correctionKey: correctionKeys[i],
    };
  });
}
